"use client";

import * as React from "react";
import { GraduationCap, CirclePlay, Map } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Academic Profiling",
  description: "We meticulously analyze your academic background to identify unique strengths that appeal to elite global universities and scholarship boards.",
  icon: GraduationCap
}, {
  title: "Strategic Matching",
  description: "Our dynamic framework aligns your ambitions with the specific funding criteria of international scholarship programs and research grants.",
  icon: CirclePlay
}, {
  title: "Institutional Trust",
  description: "Benefit from our established reputation with global admission boards to secure your place in prestigious programs with maximum financial aid.",
  icon: Map
}];
export default function TheEnlightenEdge() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="96474" id="pathways-to-excellence" className="dark bg-background py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 lg:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-6">
            The Success Framework
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            Our strategic approach to global education ensures your profile resonates with elite institutions and scholarship committees worldwide.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
          {features.map((feature, i) => <FeatureCard key={i} feature={feature} index={i} data-index={i} />)}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  feature,
  index,
  ...props
}: {
  feature: typeof features[0];
  index: number;
  [key: string]: any;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const delay = index * 150;
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${delay}ms`
  }} {...props}>
      <Card className="group h-full border-none shadow-none bg-transparent p-0 flex flex-col">
        <CardHeader className="px-0 pt-0 pb-4">
          <feature.icon className="size-12 text-primary drop-shadow-lg mb-6 transition-transform duration-300 ease-out group-hover:scale-110" strokeWidth={1.5} />
          <CardTitle className="text-2xl font-default font-bold text-foreground transition-transform duration-300 ease-out group-hover:translate-x-2">
            {feature.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0 flex-grow">
          <p className="text-base font-default text-muted-foreground leading-relaxed">
            {feature.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}